import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AngularDraggableDirective } from 'angular2-draggable';
import { IResizeEvent } from 'angular2-draggable/lib/models/resize-event';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { showAnimation } from 'src/animations';
import { Widget } from 'src/app/classes';
import { SessionService } from 'src/app/lib/shared/services/session.service';
import { WidgetService } from 'src/app/services/widget.service';
import { SelectWidgettypeComponent } from '../select-widgettype/select-widgettype.component';
import { WidgetGridCell } from '../widget-globals';
import { WidgetComponent } from '../widget/widget.component';
import { WidgetViewStandaloneComponentService, WidgetViewStandaloneModes } from './widget-view-standalone-component-service';

@Component({
  selector: 'app-widget-view-standalone',
  templateUrl: './widget-view-standalone.component.html',
  styleUrls: ['./widget-view-standalone.component.css'],
  animations: [ showAnimation ],
  providers: [ WidgetViewStandaloneComponentService ]
})
export class WidgetViewStandaloneComponent implements OnInit, OnDestroy {

  @ViewChild("startpageWidgetInner", { static: true }) body: ElementRef;

  widgetTypesLoaded: boolean = false;
  widgetTypesLoadingSubscription: Subscription;

  rows: number = 1;
  rowPercentage: string = "100%";

  grid: any;

  columns: number = 1;

  _rowHeight: number;
  _columnWidth: number;

  _deleteMode: boolean = false;
  set deleteMode(value: boolean) {
    if (!this.rightsMap.edit_dashboard) return;
    this._deleteMode = value;
    this._addWMode = { value: false, widgetType: null } as AddWModeWrapper;
  }
  get deleteMode(): boolean {
    return this._deleteMode;
  }

  _addWMode: AddWModeWrapper = {
    value: false,
    widgetType: null
  };
  set addWMode(value: AddWModeWrapper) {
    if (!this.rightsMap.edit_dashboard) return;
    this._addWMode = value;
    this._deleteMode = false;
  }
  get addWMode(): AddWModeWrapper {
    return this._addWMode;
  }

  private _widgets: Widget[];
  @Input() set widgets(widgets: Widget[]) {
    if (!widgets) return;
    this._widgets = widgets;
    this.validateCells();
  }
  get widgets(): Widget[] {
    return this._widgets;
  }

  @Output() addWidgetAnchor: EventEmitter<Widget> = new EventEmitter();
  @Output() deleteWidgetAnchor: EventEmitter<Widget> = new EventEmitter();

  usernameQueryParam: string;

  rightSubscription: Subscription;
  rightsMap = {
    "edit_dashboard": false
  };

  private _subscriptions: Subscription[] = [];

  constructor(
    public widgetViewStandaloneComponentService: WidgetViewStandaloneComponentService,
    private _dialog: MatDialog,
    public widgetService: WidgetService,
    private _sessionService: SessionService
  ) { }

  addColumn() {
    if (!this.rightsMap.edit_dashboard) return;
    this.columns++;
    for (let i = 0; i < this.rows; i++) {
      this.grid[i][this.columns - 1] = { row: i + 1, column: this.columns, blocked: false } as WidgetGridCell;
    }
  }

  addRow() {
    if (!this.rightsMap.edit_dashboard) return;
    this.rows++;
    this.setRowHeight();
    this.grid[this.rows - 1] = [];
    for (let i = 0; i < this.columns; i++) this.grid[this.rows - 1][i] = { row: this.rows, column: i + 1, blocked: false } as WidgetGridCell;
  }

  calculateGridDimensions() {
    let h = (this.body.nativeElement as HTMLDivElement).clientHeight;
    let w = (this.body.nativeElement as HTMLDivElement).scrollWidth;
    this._rowHeight = h / this.rows;
    this._columnWidth = w / this.columns;
  }

  cellClicked = (cell: WidgetGridCell) => this.widgetViewStandaloneComponentService.tryToAddWidgetAtPosition(cell);

  changeWidgettype(widget: Widget, ref: WidgetComponent) {
    this._dialog.open(SelectWidgettypeComponent, {
      panelClass: "no-padding-dialog-container",
      data: widget.widgettype
    }).afterClosed().subscribe((result: number) => {
      if (typeof (result) === 'number') {
        widget.widgettype = result;
        this.widgetService.updateWidget(widget).toPromise();
      }
    });
  }

  initRights = (soft: boolean = true) => {
    for (let index in this.rightsMap) this._sessionService.hasRight(index, soft).subscribe(result => this.rightsMap[index] = result);
  }

  ngOnDestroy(): void {
    for(let sub of this._subscriptions) sub.unsubscribe();
    this._subscriptions = [];
    this.widgetViewStandaloneComponentService.dispose();
  }

  ngOnInit(): void {
    //this._sessionService.setReadyStatus(false);
    this.widgetViewStandaloneComponentService.setUp();
    this._subscriptions.push(...[
      this.widgetViewStandaloneComponentService
        .viewMode$
        .pipe(filter(x => x === WidgetViewStandaloneModes.DRAG_AND_RESIZE_MODE))
        .subscribe(() => this.calculateGridDimensions()),
      //timer(3000).subscribe(() => this._sessionService.setReadyStatus(true))
    ]);
    this.initRights();
  }

  onMoveEnd(event, directive: AngularDraggableDirective, widget: Widget) {
    var cols = Math.round(event.x / this._columnWidth);
    var rows = Math.round(event.y / this._rowHeight);
    widget.widgety += rows;
    widget.widgetx += cols;
    directive.resetPosition();
    this.widgetService.updateWidget(widget).toPromise();
  }

  onResizeEnd(event: IResizeEvent, widget: Widget) {
    var cols = Math.round(event.size.width / this._columnWidth);
    cols ? cols : cols = 1;
    var rows = Math.round(event.size.height / this._rowHeight);
    rows ? rows : rows = 1;
    widget.widgetw = cols;
    widget.widgeth = rows;
    (event.host as HTMLDivElement).style.height = "unset";
    (event.host as HTMLDivElement).style.width = "unset";
    this.widgetService.updateWidget(widget).toPromise();
  }

  setRowHeight() {
    this.rowPercentage = (100 / this.rows) + "%";
  }

  validateCells() {
    this.rows = Math.max(...this.widgets.map(x => x.widgety + x.widgeth));
    this.columns = Math.max(...this.widgets.map(x => x.widgetx + x.widgetw));
    this.setRowHeight();
    this.grid = {};
    for (let r = 0; r < this.rows; r++) {
      let row = {};
      for (let c = 0; c < this.columns; c++) row[c.toString()] = { row: (r + 1), column: (c + 1), blocked: false } as WidgetGridCell;
      this.grid[r.toString()] = row;
    }
    for (let w of this.widgets) {
      for (let r = w.widgety; r < (w.widgety + w.widgeth); r++) {
        for (let c = w.widgetx; c < (w.widgetx + w.widgetw); c++) {
          (this.grid[r][c] as WidgetGridCell).blocked = true;
        }
      }
    }
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (this.rightsMap.edit_dashboard && event.ctrlKey) {
      if (event.key !== undefined) {
        switch (event.key) {
          case "m":
            this.widgetViewStandaloneComponentService.toggleControlBar();
            event.stopPropagation();
            event.preventDefault();
            break;

          case "Dead":
            event.stopPropagation();
            event.preventDefault();
            break;
        }
      } else if (event.which) {
        switch (event.which) {

          case 77:
            this.widgetViewStandaloneComponentService.toggleControlBar();
            event.stopPropagation();
            event.preventDefault();
            break;

          case 220:
            event.stopPropagation();
            event.preventDefault();
            break;
        }
      }
    }
  }

  WidgetViewStandaloneModes = WidgetViewStandaloneModes;
}

export class AddWModeWrapper {
  value: boolean;
  widgetType: number;
}
